import { Box, ButtonVariantProp, TextBox } from '@superdispatch/ui-lab';
import { Link, SvgIconProps, Tooltip, Typography } from '@material-ui/core';
import { useLoadsCache } from '../../carrier/loads/data/LoadsAPI';
import { ReactNode, useState } from 'react';
import { useInstantTransferPaywall } from 'shared/modules/superpay/instant-transfer/useInstantTransferPaywall';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { LockedIcon } from 'shared/icons/LockIcon';
import { UpgradePlanDialog } from '../../carrier/loads/core/UpgradePlanDialog';
import { InstantTransferDialog } from 'shared/modules/superpay/instant-transfer/InstantTransferDialog';
import styled from 'styled-components';
import { Color, Column, Columns, Stack } from '@superdispatch/ui';
import { LoadPaymentDTO } from '../../carrier/loads/data/LoadDTO';
import { Info } from '@material-ui/icons';

const OnHoldReasonList = styled.ul`
  padding-inline-start: 24px;
  margin: 0;
`;

const OnHoldReasonListItem = styled.li`
  margin-bottom: 4px;
`;

const OnHoldReasonTooltipHeader = styled(Typography)`
  color: ${Color.Yellow300};
`;

const CustomHoldReason = styled(Typography)`
  word-break: break-word;
  hyphens: auto;
`;

const TooltipIconWrapper = styled.span`
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

const StyledButtonWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;

  button {
    padding: 2px 16px;
  }
`;

export function InstantTransferStatus({
  loadGUID,
  source,
  buttonVariant,
  icon: Icon,
}: {
  loadGUID: string;
  source: 'Load List' | 'Load Details';
  buttonVariant: ButtonVariantProp;
  icon?: React.FunctionComponent<SvgIconProps>;
}) {
  const { invalidateLoads } = useLoadsCache();

  const [dialog, setDialog] = useState<'instant-transfer' | 'upgrade' | null>(
    null,
  );

  const { shouldShowUpgradeDialog: shouldShowLockIcon } =
    useInstantTransferPaywall();

  return (
    <>
      <StyledButtonWrapper>
        <PermissionButton
          startIcon={
            shouldShowLockIcon ? <LockedIcon /> : Icon ? <Icon /> : undefined
          }
          name="instant_transfer"
          permission="can_view"
          variant={buttonVariant}
          onClick={() => {
            if (shouldShowLockIcon) {
              setDialog('upgrade');
            } else {
              setDialog('instant-transfer');
            }
          }}
        >
          Get Paid Now
        </PermissionButton>
      </StyledButtonWrapper>
      <UpgradePlanDialog
        open={dialog === 'upgrade'}
        onClose={() => setDialog(null)}
        type="instant_transfer"
        source={source}
      />
      <InstantTransferDialog
        open={dialog === 'instant-transfer'}
        onClose={() => {
          setDialog(null);
          invalidateLoads();
        }}
        loadGUID={loadGUID}
        source={source}
      />
    </>
  );
}

export function StatusWrapper({
  children,
  icon,
}: {
  children?: ReactNode | null;
  icon: ReactNode | null;
}) {
  return (
    <Columns space="xxsmall" align="top">
      {icon && (
        <Column width="content">
          <Box position="relative" top="2px">
            {icon}
          </Box>
        </Column>
      )}
      {children && <Column>{children}</Column>}
    </Columns>
  );
}

export function StatusInfoTooltip({ payment }: { payment: LoadPaymentDTO }) {
  return (
    <Tooltip
      arrow={true}
      interactive={true}
      title={
        <Stack>
          <Typography>{payment.status_info}</Typography>
          {payment.status_info_url && (
            <Link
              color="inherit"
              target="_blank"
              rel="noreferrer noopener"
              href={payment.status_info_url}
            >
              Learn more
            </Link>
          )}
        </Stack>
      }
    >
      <TooltipIconWrapper>
        <Info color="action" fontSize="small" aria-label="details icon" />
      </TooltipIconWrapper>
    </Tooltip>
  );
}

interface OnHoldReasonTooltipProps {
  payment: LoadPaymentDTO;
}
export function OnHoldReasonTooltip({ payment }: OnHoldReasonTooltipProps) {
  const {
    custom_on_hold_reason,
    on_hold_reasons,
    status_info,
    status_details,
  } = payment;

  const isCustomHoldReasonAvailable = !!custom_on_hold_reason;
  const isHoldReasonsAvailable =
    !!on_hold_reasons && on_hold_reasons.length > 0;

  return (
    <Tooltip
      arrow={true}
      aria-label="On-hold details"
      enterTouchDelay={0}
      title={
        <Box maxWidth="309px">
          <Stack space="xsmall">
            <OnHoldReasonTooltipHeader variant="h5">
              {status_details}
            </OnHoldReasonTooltipHeader>
            <Stack space="small">
              {isCustomHoldReasonAvailable ? (
                <CustomHoldReason>{custom_on_hold_reason}</CustomHoldReason>
              ) : isHoldReasonsAvailable ? (
                <OnHoldReasonList>
                  {on_hold_reasons.map((reason) => (
                    <OnHoldReasonListItem key={reason}>
                      {reason}
                    </OnHoldReasonListItem>
                  ))}
                </OnHoldReasonList>
              ) : (
                <TextBox color="secondary">
                  Reason wasn&apos;t provided.
                </TextBox>
              )}
              <Columns space="xsmall" align="top">
                <Column width="content">
                  <Info fontSize="small" color="action" />
                </Column>
                <Column>
                  <TextBox color="secondary">{status_info}</TextBox>
                </Column>
              </Columns>
            </Stack>
          </Stack>
        </Box>
      }
    >
      <TooltipIconWrapper>
        <Info
          aria-label="On-hold details icon"
          color="action"
          fontSize="small"
        />
      </TooltipIconWrapper>
    </Tooltip>
  );
}
